<script>
    import {
        Button,
        CardLayout,
        Loader,
        NotificationModal,
        SvelteTable,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        addIcon,
        deleteIcon,
        editIcon,
    } from "../../../Utils/utilConstants.svelte";

    import {
        getText,
        isObjectEmpty,
        showError,
    } from "../../../Utils/utilFunctions.svelte";

    import AccountEditPipVendorModal from "./AccountEditPipVendorModal.svelte";

    export let providerDetails = [];
    export let reload = () => {};

    $: if (providerDetails && providerDetails.length && !isTableDataLoaded)
        _syncProviderPipVendorNumber();

    let vendorsArr = [];

    let isLoadingHospitalsDetail = false,
        isSyncingHospitalName = false;

    let selectedVendor = {},
        isNewVendor = true,
        isEditVendorModalOpen = false;

    let isDeleteVendorModalOpen = false,
        isDeletingVendor = false;

    let isTableDataLoaded = false;

    const btnOptions = [
        {
            iconName: "addIcon",
            hasTooltip: false,
            tooltip: "Add Vendor",
            iFont: addIcon,
            iconFunction: () => _openEditVendorModal(),
            btnText: "Add",
        },
    ];

    const columns = [
        {
            key: "number",
            title: "Provider Number",
            value: (v) => getText(v.providerNumber),
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-auto",
        },
        {
            key: "hospitalName",
            title: "Hospital Name",
            value: (v) => getText(v.hospitalName),
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-auto",
        },
        {
            key: "vendorNumber",
            title: "Public in Private Vendor Number",
            value: (v) => getText(v.pipVendorNumber),
            headerClass: "text-left pl-4 text-[14px]",
            class: "text-left pl-4 text-[14px] cursor-auto",
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "px-3 cursor-pointer",
            renderValue: () => _renderActionIcons(),
        },
    ];

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "buttons") {
            if (className.includes("editIcon")) _openEditVendorModal(rowData);
            else if (className.includes("deleteIcon"))
                _openDeleteVendorModal(rowData);
        }
    };

    const deleteVendor = async () => {
        isDeletingVendor = true;

        const providerDetails = _syncProviderDetails();

        await apiMethods.putMethod
            .updateAccountProvider(providerDetails)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete selected vendor, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    reload();
                    isTableDataLoaded = false;
                }
            })
            .catch((err) => showError(err));

        isDeletingVendor = false;
    };

    // HELPER FUNCTIONS
    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Vendor">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Vendor">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _syncProviderPipVendorNumber = async () => {
        const hospitalIdsArr = [];

        providerDetails.forEach(({ number, hospitals }) => {
            hospitals.forEach(({ hospitalId, pipVendorNumber }) => {
                vendorsArr.push({
                    providerNumber: number,
                    hospitalId,
                    pipVendorNumber,
                });

                if (!hospitalIdsArr.includes(hospitalId))
                    hospitalIdsArr.push(hospitalId);
            });
        });

        btnOptions[0].isHidden = vendorsArr.length == 0;

        isLoadingHospitalsDetail = true;

        if (hospitalIdsArr.length) await _syncHospitalName(hospitalIdsArr);

        isLoadingHospitalsDetail = false;

        isTableDataLoaded = true;
    };

    const _syncHospitalName = async (arr) => {
        if (isSyncingHospitalName) return;

        isSyncingHospitalName = true;

        await apiMethods.getMethod
            .getHospitals(0, 999999, { "filter[id]": arr.join(",") })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to load hospital details, please try again later or contact support...",
                    );
                else
                    vendorsArr = vendorsArr.map((vendor) => {
                        const hospital = res.results.find(
                            (hospital) => hospital.id == vendor.hospitalId,
                        );

                        if (hospital)
                            return {
                                ...vendor,
                                hospitalName:
                                    hospital.name +
                                    `${hospital.state ? `, ${hospital.state}` : ""}`,
                            };

                        return vendor;
                    });
            });

        isSyncingHospitalName = false;
    };

    const _syncProviderDetails = () => {
        const selectedProvider = providerDetails.find(
            (provider) => provider.number == selectedVendor.providerNumber,
        );

        selectedProvider.hospitals = selectedProvider.hospitals.filter(
            (hospital) => hospital.hospitalId != selectedVendor.hospitalId,
        );

        return selectedProvider;
    };

    const _openEditVendorModal = (data = {}) => {
        selectedVendor = { ...data };
        isEditVendorModalOpen = true;

        if (isObjectEmpty(selectedVendor)) isNewVendor = true;
        else isNewVendor = false;
    };

    const _openDeleteVendorModal = (data = {}) => {
        selectedVendor = { ...data };
        isDeleteVendorModalOpen = true;
    };

    const _closeEditVendorModal = () => {
        selectedVendor = {};
        isEditVendorModalOpen = false;
        isNewVendor = true;
    };

    const _closeDeleteVendorModal = () => {
        selectedVendor = {};
        isDeleteVendorModalOpen = false;
    };
</script>

<CardLayout title="Public In Private Vendor Numbers" {btnOptions}>
    {#if isLoadingHospitalsDetail}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div
                class="flex items-center gap-x-4 flex-1 px-2 py-4 w-full lg:flex-row"
            >
                <Loader />
                <p>Loading Vendor Details...</p>
            </div>
        </div>
    {:else if vendorsArr.length}
        <div class="flex flex-1 overflow-auto">
            <SvelteTable
                {columns}
                rows={vendorsArr}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
            />
        </div>
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div
                class="flex items-center justify-between flex-1 px-2 py-4 w-full lg:flex-row"
            >
                <p>No vendors under account</p>

                <Button clickFunc={() => _openEditVendorModal()}>
                    Add vendor
                </Button>
            </div>
        </div>
    {/if}
</CardLayout>

<AccountEditPipVendorModal
    {providerDetails}
    vendorDetails={selectedVendor}
    isModalOpen={isEditVendorModalOpen}
    closeModal={_closeEditVendorModal}
    {isNewVendor}
    reload={() => {
        reload();
        isTableDataLoaded = false;
    }}
/>

<NotificationModal
    isModalOpen={isDeleteVendorModalOpen}
    title="Delete"
    cancelText="Delete"
    submitText="Cancel"
    closeModal={deleteVendor}
    confirmAction={_closeDeleteVendorModal}
    isConfirming={isDeletingVendor}
    closeModalFunction={_closeDeleteVendorModal}
    isReverseBtns
    confirmButtonClass="btn-accent"
>
    Delete selected provider hospital?
</NotificationModal>
